import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {getPublishedPosts, getFeaturedPosts, getPostsByCategory, searchPosts} from '../utils/blogStorage';

const {FiCalendar, FiUser, FiArrowRight, FiTag, FiSearch, FiLoader, FiBookOpen, FiClock, FiEye} = FiIcons;

const Blog = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    'AI in Software Development',
    'Prompt Engineering Best Practices',
    'Agile & AI',
    'Future Tech Trends',
    'Case Study Deep Dives'
  ];

  useEffect(() => {
    loadPosts();
  }, [activeCategory, searchQuery]);

  const loadPosts = async () => {
    try {
      setIsLoading(true);
      setError(null);

      let filteredPosts = [];
      if (searchQuery) {
        filteredPosts = await searchPosts(searchQuery);
      } else if (activeCategory === 'all') {
        filteredPosts = await getPublishedPosts();
      } else {
        filteredPosts = await getPostsByCategory(activeCategory);
      }

      setPosts(filteredPosts);

      // Only load featured posts when showing all posts
      if (activeCategory === 'all' && !searchQuery) {
        const featured = await getFeaturedPosts();
        setFeaturedPosts(featured);
      } else {
        setFeaturedPosts([]);
      }

      console.log(`Loaded ${filteredPosts.length} posts for category: ${activeCategory}`);
    } catch (error) {
      console.error('Error loading posts:', error);
      setError('Failed to load blog posts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setSearchQuery('');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadPosts();
  };

  const handleReadMore = (post) => {
    navigate(`/blog/${post.id}`);
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    if (email) {
      alert(`Thank you for subscribing with ${email}! You'll receive our latest AI development insights.`);
      e.target.reset();
    }
  };

  const handleScheduleConsultation = () => {
    navigate('/contact');
  };

  const handleViewPortfolio = () => {
    navigate('/case-studies');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          className="text-center"
        >
          <SafeIcon icon={FiLoader} className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading blog posts...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadPosts}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 40}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Insights & Innovations
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              The Future of AI in Development. Stay informed with the latest trends, expert analyses, and practical advice on AI-accelerated coding, prompt engineering, and the evolving landscape of software development.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search articles..."
                  className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <SafeIcon icon={FiSearch} className="absolute left-4 top-3.5 h-5 w-5 text-gray-400" />
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => handleCategoryChange('all')}
              className={`px-6 py-2 rounded-full font-medium transition-colors ${
                activeCategory === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 text-gray-700 hover:border-blue-600 hover:text-blue-600'
              }`}
            >
              All Articles
            </button>
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => handleCategoryChange(category)}
                className={`px-6 py-2 rounded-full font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'border border-gray-300 text-gray-700 hover:border-blue-600 hover:text-blue-600'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && activeCategory === 'all' && !searchQuery && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{opacity: 0, y: 40}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8}}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Featured Articles</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {featuredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{opacity: 0, y: 40}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true}}
                    transition={{duration: 0.6, delay: index * 0.1}}
                    className="group cursor-pointer"
                    onClick={() => handleReadMore(post)}
                  >
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105 lg:flex lg:items-center">
                      <div className="lg:w-1/2">
                        <img
                          src={post.image || 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=600&h=400&fit=crop'}
                          alt={post.title}
                          className="w-full h-64 lg:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-6 lg:w-1/2 lg:p-8">
                        <div className="flex items-center mb-4">
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full">
                            {post.category}
                          </span>
                          <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-3 py-1 rounded-full ml-2">
                            Featured
                          </span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 mb-4 leading-relaxed">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                              <SafeIcon icon={FiUser} className="h-4 w-4 mr-1" />
                              {post.author}
                            </div>
                            <div className="flex items-center">
                              <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-1" />
                              {post.date}
                            </div>
                          </div>
                          <span>{post.readTime}</span>
                        </div>
                        <div className="mt-4">
                          <span className="inline-flex items-center text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
                            Read Article
                            <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* All Posts */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 40}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              {searchQuery 
                ? `Search Results (${posts.length})` 
                : activeCategory === 'all' 
                ? 'All Articles' 
                : `${activeCategory} Articles`
              }
            </h2>

            {posts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{opacity: 0, y: 40}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true}}
                    transition={{duration: 0.6, delay: index * 0.1}}
                    className="group cursor-pointer"
                    onClick={() => handleReadMore(post)}
                  >
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                      <div className="relative">
                        <img
                          src={post.image || 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=300&fit=crop'}
                          alt={post.title}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {post.featured && (
                          <div className="absolute top-3 right-3">
                            <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                              Featured
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center mb-3">
                          <SafeIcon icon={FiTag} className="h-4 w-4 text-blue-600 mr-2" />
                          <span className="text-blue-600 text-sm font-medium">{post.category}</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                          <div className="flex items-center">
                            <SafeIcon icon={FiUser} className="h-4 w-4 mr-1" />
                            {post.author}
                          </div>
                          <div className="flex items-center">
                            <SafeIcon icon={FiClock} className="h-4 w-4 mr-1" />
                            {post.readTime}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-gray-500">
                            <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-1" />
                            {post.date}
                          </div>
                          <span className="inline-flex items-center text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
                            <SafeIcon icon={FiBookOpen} className="mr-1 h-4 w-4" />
                            Read
                            <SafeIcon icon={FiArrowRight} className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <SafeIcon icon={FiSearch} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {searchQuery ? 'No articles found' : 'No articles yet'}
                </h3>
                <p className="text-gray-600">
                  {searchQuery 
                    ? 'Try adjusting your search terms or browse all articles' 
                    : 'Check back soon for new content!'
                  }
                </p>
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory('all');
                    }}
                    className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View all articles
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0, y: 40}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Stay Updated with AI Development Insights
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Get the latest articles, case studies, and AI development tips delivered directly to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                name="email"
                required
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
              />
              <button
                type="submit"
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                Subscribe
              </button>
            </form>
            <p className="text-blue-100 text-sm mt-4">
              No spam, unsubscribe at any time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0, y: 40}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Transform Your Development Process?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Choose from our comprehensive AI-accelerated services or let us create a custom solution for your unique needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleScheduleConsultation}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Schedule a Consultation
              </button>
              <button
                onClick={handleViewPortfolio}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-600 hover:text-white transition-all duration-300"
              >
                View Our Portfolio
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;